/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH
 * under one or more contributor license agreements. Licensed under a proprietary license.
 * See the License.txt file for more information. You may not use this file
 * except in compliance with the proprietary license.
 */

import {ReactNode} from 'react';
import {getLanguage, t} from './translation';

type Handler = {
  fct: (response: Response, payload: RequestPayload) => Promise<Response>;
  priority: number;
};

export type RequestPayload = {
  url: string;
  method: string;
  body?: unknown;
  query?: Record<string, unknown>;
  headers?: Record<string, unknown>;
};

export interface ErrorResponse extends Error {
  status: number;
  /** Plain-text message — safe for Error.message, catch blocks, and logging. */
  message: string;
  /**
   * UI-renderable content for notification components.
   * May be a string or a React element with rich formatting.
   * Use this field when displaying the error to the user;
   * use `message` when logging or passing to Error constructors.
   */
  displayMessage: ReactNode;
  reportDefinition?: unknown;
  conflictedItems?: unknown[];
  [key: string]: unknown;
}

/**
 * A proper Error subclass so that:
 * 1. `instanceof Error` checks pass (React error boundaries, try/catch)
 * 2. TestCafe sees a real Error with a message rather than [object Object]
 * 3. Stack traces are preserved
 */
export class RequestError extends Error implements ErrorResponse {
  status: number;
  displayMessage: ReactNode;
  reportDefinition?: unknown;
  conflictedItems?: unknown[];
  [key: string]: unknown;

  constructor(status: number, displayMessage: ReactNode, extra: Record<string, unknown> = {}) {
    super(typeof displayMessage === 'string' ? displayMessage : 'Request error');
    this.name = 'RequestError';
    this.status = status;
    this.displayMessage = displayMessage;
    Object.assign(this, extra);
  }
}

const handlers: Handler[] = [];

export function put(
  url: string,
  body: unknown,
  options: Record<string, unknown> = {}
): Promise<Response> {
  return request({
    url,
    body,
    method: 'PUT',
    ...options,
  });
}

export function post(
  url: string,
  body?: unknown,
  options: Record<string, unknown> = {}
): Promise<Response> {
  return request({
    url,
    body,
    method: 'POST',
    ...options,
  });
}

export function get(
  url: string,
  query?: Record<string, unknown>,
  options: Record<string, unknown> = {}
): Promise<Response> {
  return request({
    url,
    query,
    method: 'GET',
    ...options,
  });
}

export function del(
  url: string,
  query?: Record<string, unknown>,
  options: Record<string, unknown> = {}
): Promise<Response> {
  return request({
    url,
    query,
    method: 'DELETE',
    ...options,
  });
}

export function addHandler(fct: Handler['fct'], priority = 0) {
  handlers.push({fct, priority});
  handlers.sort((a, b) => b.priority - a.priority);
}

export function removeHandler(fct: Handler['fct']) {
  const handlerToRemove = handlers.find((entry) => entry.fct === fct);
  if (handlerToRemove) {
    handlers.splice(handlers.indexOf(handlerToRemove), 1);
  }
}

export async function request(payload: RequestPayload): Promise<Response> {
  const {url, method, body, query, headers} = payload;
  const resourceUrl = query ? `${url}?${formatQuery(query)}` : url;

  let response = await fetch(resourceUrl, {
    method,
    body: processBody(body),
    headers: {
      'Content-Type': 'application/json',
      'X-Optimize-Client-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'X-Optimize-Client-Locale': getLanguage(),
      ...headers,
    },
    mode: 'cors',
    credentials: 'same-origin',
  });

  for (const handlerToCall of handlers) {
    if (handlerToCall) {
      response = await handlerToCall.fct(response, payload);
    }
  }

  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    throw await parseError(response);
  }
}

export function formatQuery(query: Record<string, unknown>): string {
  return Object.keys(query)
    .reduce<string[]>((queryStr, key) => {
      const value = query[key] as string | string[];

      if (Array.isArray(value)) {
        const str = value.map((val) => `${key}=${val}`).join('&');
        if (!str) {
          return queryStr;
        }
        return queryStr.concat(str);
      }

      if (queryStr.length === 0) {
        return [`${key}=${encodeURIComponent(value)}`];
      }

      return queryStr.concat(`${key}=${encodeURIComponent(value)}`);
    }, [])
    .join('&');
}

function processBody(body: unknown): string {
  if (typeof body === 'string') {
    return body;
  }

  return JSON.stringify(body);
}

async function parseError(error: Response): Promise<RequestError> {
  let displayMessage: ReactNode = error.statusText || 'Unknown error';
  if (error.status === 413) {
    return new RequestError(error.status, t('apiErrors.payloadTooLarge'));
  }

  const errorAsRecord = error as unknown as Record<string, unknown>;

  if (typeof errorAsRecord.json !== 'function') {
    if (typeof errorAsRecord.text === 'function') {
      try {
        const text = await (error as unknown as {text: () => Promise<string>}).text();
        if (text) {
          displayMessage = text;
        }
      } catch {
        // body unreadable — statusText fallback is already in `displayMessage`
      }
    }
    return new RequestError(error.status, displayMessage);
  }

  try {
    const {errorCode, errorMessage, ...errorProps} = await (
      error as unknown as {json: () => Promise<Record<string, unknown>>}
    ).json();
    if (errorMessage) {
      displayMessage = errorMessage as string;
    }

    if (errorCode) {
      try {
        displayMessage = t('apiErrors.' + errorCode, {...(errorProps as Record<string, unknown>)});
      } catch (e) {
        console.error('Tried to parse error message, but failed: ', error, e);
      }
    }

    return new RequestError(error.status, displayMessage, errorProps as Record<string, unknown>);
  } catch (e) {
    console.error('Tried to parse error object, but failed', error, e);
  }

  return new RequestError(error.status, displayMessage);
}
